import { gatewayMessage } from './gateway-message';

/**
 * Browser-side Payme Subscribe (Cards API) client. Tokenizes + OTP-verifies a
 * card by calling Payme DIRECTLY from the browser (X-Auth = the public subscribe
 * cassa id), so the raw PAN never reaches our backend (SAQ A-EP). We then post
 * only the verified token to our API (see billing-client `saveCard`).
 */
const env = import.meta.env as Record<string, string | undefined>;
const CHECKOUT_URL = (
  env.VITE_PAYME_SUBSCRIBE_CHECKOUT_URL ?? 'https://checkout.paycom.uz'
).replace(/\/+$/, '');
const CASSA_ID = env.VITE_PAYME_SUBSCRIBE_CASSA_ID ?? '';
const BASE = `${CHECKOUT_URL}/api`;

/**
 * ⚠️ LOCAL-DEV ONLY. When running `vite dev` with NO Payme cassa configured
 * (`VITE_PAYME_SUBSCRIBE_CASSA_ID` empty), the real cross-origin call to
 * checkout.paycom.uz cannot succeed, so the card flow can never reach the OTP
 * screen to review. This short-circuits the three Cards-API calls with fakes so
 * the UI advances card → OTP → done for design review.
 *
 * Double-guarded so it is impossible in production: `import.meta.env.DEV` is
 * false in any built bundle, AND prod always has a cassa id (so the condition is
 * false regardless). Drop this before shipping if you prefer, but it is inert in
 * prod either way.
 */
const MOCK_CARDS = import.meta.env.DEV && !CASSA_ID;

export interface TokenizedCard {
  token: string;
  maskedPan: string;
}

export class PaymeCardError extends Error {
  constructor(
    readonly code: number,
    message: string,
  ) {
    super(message);
    this.name = 'PaymeCardError';
  }
}

interface RpcCardResult {
  card: { token: string; number: string; verify?: boolean };
}

export { gatewayMessage } from './gateway-message';

async function rpc<T>(method: string, params: Record<string, unknown>): Promise<T> {
  const res = await fetch(BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Auth': CASSA_ID },
    body: JSON.stringify({ id: 1, method, params }),
  });
  const body = (await res.json()) as {
    result?: T;
    error?: { code: number; message: string | Record<string, string> };
  };
  if (body.error) {
    throw new PaymeCardError(body.error.code, gatewayMessage(body.error.message));
  }
  return body.result as T;
}

/** Register the card → unverified token + masked PAN. `expire` is MMYY. */
export async function cardsCreate(cardNumber: string, expire: string): Promise<TokenizedCard> {
  if (MOCK_CARDS) {
    const d = cardNumber.replace(/\D/g, '');
    return {
      token: 'mock_token',
      maskedPan: `${d.slice(0, 4)} ${d.slice(4, 6)}** **** ${d.slice(-4)}`,
    };
  }
  const r = await rpc<RpcCardResult>('cards.create', {
    card: { number: cardNumber.replace(/\s+/g, ''), expire },
    save: true,
  });
  return { token: r.card.token, maskedPan: r.card.number };
}

/**
 * Ask Payme to SMS the cardholder an OTP for the token. Returns the masked phone
 * Payme sent it to (`{ phone }`) when present, so the confirm screen can name it;
 * `undefined` when Payme omits it (fall back to a generic line).
 *
 * `waitMs` is Payme's own «ask again after N ms» (`wait`, 60 000 in practice).
 * A request inside it sends no SMS (owner's iPhone, 2026-10-17: a resend at
 * 22 s brought nothing, one at 1:45 brought the code), so the sheet's countdown
 * follows it whenever Payme sends one.
 */
export async function cardsGetVerifyCode(
  token: string,
): Promise<{ phone?: string; waitMs?: number }> {
  if (MOCK_CARDS) return { phone: '+998 90 ••• •• 45' }; // demo phone for review
  const r = await rpc<{ sent?: boolean; phone?: string; wait?: number }>('cards.get_verify_code', {
    token,
  });
  const waitMs = typeof r?.wait === 'number' && r.wait > 0 ? r.wait : undefined;
  return { phone: r?.phone, waitMs };
}

/** Verify the token with the OTP → chargeable token. */
export async function cardsVerify(token: string, code: string): Promise<TokenizedCard> {
  if (MOCK_CARDS) return { token: 'mock_verified_token', maskedPan: '8600 06** **** 0000' };
  const r = await rpc<RpcCardResult>('cards.verify', { token, code });
  return { token: r.card.token, maskedPan: r.card.number };
}
